import axios from 'axios';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import { currentOpsAgencyId } from './agency';
import { schemas } from './generated/schemas';
import type {
  ManagerCreateInput,
  ManagerStatus,
  ManagerUpdateInput,
  OnDutyData,
  OpsManager,
  RepeatResult,
  ScheduleCreateInput,
  ScheduleCreateResult,
  ScheduleRepeatInput,
  SchedulesData,
  ScheduleUpdateInput,
  ScheduleWriteResult,
} from './types';

// REST client for the ops rota (GRAVY-10878): /api/admin/ops_on_duty,
// /ops_managers and /ops_schedules. Each response is parsed with the schema
// generated from graphy's contract, so drift fails fast. No GraphQL.

const FALLBACK_MESSAGE = 'Something went wrong. Try again.';

export class OpsRotaApiError extends Error {
  readonly status: number | null;

  readonly messages: string[];

  constructor(status: number | null, messages: string[]) {
    super(messages[0] ?? FALLBACK_MESSAGE);
    // Keeps instanceof working when Error subclasses are down-levelled.
    Object.setPrototypeOf(this, OpsRotaApiError.prototype);
    this.name = 'OpsRotaApiError';
    this.status = status;
    this.messages = messages;
  }

  /** 409: the block changed since it was loaded. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

// HTTP failures become OpsRotaApiError carrying graphy's `errors`; anything
// else (a schema mismatch, a bug) is rethrown untouched so it stays visible.
const call = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (err) {
    if (!axios.isAxiosError(err)) throw err;
    const errors = (err.response?.data as { errors?: unknown } | undefined)
      ?.errors;
    const messages =
      Array.isArray(errors) && errors.length > 0
        ? errors.map(String)
        : [FALLBACK_MESSAGE];
    throw new OpsRotaApiError(err.response?.status ?? null, messages);
  }
};

const compact = (params: Record<string, string | null | undefined>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== ''),
  );

/**
 * Query params or a body plus `agency_id`, the shell's current agency.
 * Left out until the shell has resolved it.
 */
const scoped = <T extends object>(params: T): T & { agency_id?: number } => {
  const agencyId = currentOpsAgencyId();
  return agencyId === null ? params : { ...params, agency_id: agencyId };
};

export type ListSchedulesParams = {
  from: string;
  to: string;
  /**
   * Also the day before `from`'s overnight blocks that run into
   * `from` (flagged `carry_over`). The day panel and the preview card ask
   * for them; the month grid does not (dots stay on the start date).
   */
  includeCarryOver?: boolean;
};

export const opsRotaClient = {
  getOnDuty(params: {
    timeZone?: string;
    date?: string | null;
  }): Promise<OnDutyData> {
    return call(async () => {
      const { data } = await axiosRubyClient.get('/api/admin/ops_on_duty', {
        params: scoped(
          compact({ time_zone: params.timeZone, date: params.date }),
        ),
      });
      const parsed = schemas.on_duty_response.parse(data);
      return { entries: parsed.data, meta: parsed.meta };
    });
  },

  listSchedules({
    from,
    to,
    includeCarryOver = false,
  }: ListSchedulesParams): Promise<SchedulesData> {
    return call(async () => {
      const { data } = await axiosRubyClient.get('/api/admin/ops_schedules', {
        params: scoped({
          from,
          to,
          ...(includeCarryOver ? { include_carry_over: true } : {}),
        }),
      });
      const parsed = schemas.schedules_response.parse(data);
      return { schedules: parsed.data, meta: parsed.meta };
    });
  },

  createSchedule(input: ScheduleCreateInput): Promise<ScheduleCreateResult> {
    return call(async () => {
      const { data } = await axiosRubyClient.post(
        '/api/admin/ops_schedules',
        scoped(input),
      );
      const parsed = schemas.create_schedule_response.parse(data);
      return {
        schedule: parsed.data,
        merged: parsed.meta.merged,
        dates: parsed.meta.dates,
      };
    });
  },

  updateSchedule(
    id: number,
    input: ScheduleUpdateInput,
  ): Promise<ScheduleWriteResult> {
    return call(async () => {
      const { data } = await axiosRubyClient.patch(
        `/api/admin/ops_schedules/${id}`,
        scoped(input),
      );
      const parsed = schemas.schedule_response.parse(data);
      return { schedule: parsed.data, merged: parsed.meta.merged };
    });
  },

  deleteSchedule(id: number, lockVersion: number): Promise<void> {
    return call(async () => {
      const { data } = await axiosRubyClient.delete(
        `/api/admin/ops_schedules/${id}`,
        { params: scoped({ lock_version: lockVersion }) },
      );
      schemas.delete_response.parse(data);
    });
  },

  repeatSchedule(
    id: number,
    input: ScheduleRepeatInput,
  ): Promise<RepeatResult> {
    return call(async () => {
      const { data } = await axiosRubyClient.post(
        `/api/admin/ops_schedules/${id}/repeat`,
        scoped(input),
      );
      return schemas.repeat_response.parse(data).data;
    });
  },

  /** `active` (default) feeds the schedule picker; `all` the roster modal. */
  listManagers(params: { status?: ManagerStatus } = {}): Promise<OpsManager[]> {
    return call(async () => {
      const { data } = await axiosRubyClient.get('/api/admin/ops_managers', {
        params: scoped({ status: params.status ?? 'active' }),
      });
      return schemas.managers_response.parse(data).data;
    });
  },

  getManager(id: number): Promise<OpsManager> {
    return call(async () => {
      const { data } = await axiosRubyClient.get(
        `/api/admin/ops_managers/${id}`,
        { params: scoped({}) },
      );
      return schemas.manager_response.parse(data).data;
    });
  },

  createManager(input: ManagerCreateInput): Promise<OpsManager> {
    return call(async () => {
      const { data } = await axiosRubyClient.post(
        '/api/admin/ops_managers',
        scoped(input),
      );
      return schemas.manager_response.parse(data).data;
    });
  },

  updateManager(id: number, patch: ManagerUpdateInput): Promise<OpsManager> {
    return call(async () => {
      const { data } = await axiosRubyClient.patch(
        `/api/admin/ops_managers/${id}`,
        scoped(patch),
      );
      return schemas.manager_response.parse(data).data;
    });
  },

  /** 422 when the manager still has schedules: deactivate instead. */
  deleteManager(id: number): Promise<void> {
    return call(async () => {
      const { data } = await axiosRubyClient.delete(
        `/api/admin/ops_managers/${id}`,
        { params: scoped({}) },
      );
      schemas.manager_delete_response.parse(data);
    });
  },
};
